import { useState, useEffect, useCallback, useRef } from "react";
import {
  Box, Typography, Button, TextField, InputAdornment,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, IconButton, Tooltip, MenuItem, Select, FormControl,
  Pagination, CircularProgress, Alert, Dialog,
  DialogActions, DialogContent, DialogTitle,
  Avatar, Switch, Stack,
} from "@mui/material";
import SearchIcon                from "@mui/icons-material/Search";
import AddIcon                   from "@mui/icons-material/Add";
import EditIcon                  from "@mui/icons-material/Edit";
import DeleteIcon                from "@mui/icons-material/Delete";
import LockResetIcon             from "@mui/icons-material/LockReset";
import VisibilityOutlinedIcon    from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import ShieldOutlinedIcon        from "@mui/icons-material/ShieldOutlined";
import InfoOutlinedIcon          from "@mui/icons-material/InfoOutlined";
import api from "../../../services/api";

/* ─── Theme ──────────────────────────────────────────────────── */
const C = {
  primary:    "#111827",
  accent:     "#2563EB",
  accentBg:   "#EFF6FF",
  surface:    "#FFFFFF",
  border:     "#E5E7EB",
  text1:      "#111827",
  text2:      "#4B5563",
  text3:      "#9CA3AF",
  success:    "#059669",
  successBg:  "#ECFDF5",
  danger:     "#DC2626",
  dangerBg:   "#FEF2F2",
  warn:       "#D97706",
  warnBg:     "#FFFBEB",
};

const inputBase = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px", backgroundColor: "#fff",
    fontSize: "0.85rem", color: C.text1,
    "& fieldset": { borderColor: C.border, borderWidth: "1px" },
    "&:hover fieldset": { borderColor: C.text3 },
    "&.Mui-focused fieldset": { borderColor: C.accent, borderWidth: "1.5px" },
  },
  "& .MuiInputLabel-root": { fontSize: "0.85rem", color: C.text3 },
  "& .MuiInputLabel-root.Mui-focused": { color: C.accent },
};

/* ─── Helpers ────────────────────────────────────────────────── */
function getInitials(name) {
  if (!name) return "?";
  return name.split(" ").slice(0, 2).map((n) => n[0]).join("").toUpperCase();
}

function getAvatarColor(name) {
  const colors = ["#2563EB", "#7C3AED", "#059669", "#D97706", "#DC2626", "#0891B2"];
  if (!name) return colors[0];
  return colors[name.charCodeAt(0) % colors.length];
}

function RoleChip({ roleName }) {
  if (!roleName) return <Typography sx={{ fontSize: "0.78rem", color: C.text3 }}>—</Typography>;
  const map = {
    superadmin: { bg: "#F5F3FF", color: "#7C3AED", label: "Superadmin" },
    admin:      { bg: C.warnBg,  color: C.warn,    label: "Admin"       },
    user:       { bg: C.accentBg, color: C.accent, label: "User"        },
  };
  const s = map[roleName.toLowerCase()] ?? { bg: "#F3F4F6", color: C.text2, label: roleName };
  return (
    <Box sx={{
      display: "inline-flex", px: 1.5, py: 0.3, borderRadius: "20px",
      fontSize: "0.72rem", fontWeight: 700, bgcolor: s.bg, color: s.color,
      textTransform: "uppercase", letterSpacing: "0.02em",
    }}>
      {s.label}
    </Box>
  );
}
const EMPTY_FORM = { name: "", email: "", password: "", role_id: "", is_active: true };
const EMPTY_RESET = { password: "", confirm: "" };

/* ─── Auto-dismiss alert ─────────────────────────────────────── */
function AutoAlert({ severity, message, onClose }) {
  useEffect(() => {
    if (!message) return;
    const t = setTimeout(onClose, 4000);
    return () => clearTimeout(t);
  }, [message, onClose]);
  if (!message) return null;
  return (
    <Alert severity={severity} onClose={onClose} sx={{ mb: 3, borderRadius: "12px" }}>
      {message}
    </Alert>
  );
}

/* ═══════════════════════════════════════════════════════════════ */
export default function UserManagement() {
  const [users,       setUsers]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState("");
  const [success,     setSuccess]     = useState("");
  const [page,        setPage]        = useState(1);
  const [totalPages,  setTotalPages]  = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search,      setSearch]      = useState("");
  // FIX: filterRole pakai string kosong agar konsisten dengan value Select
  const [filterRole,  setFilterRole]  = useState("");
  const [roles,       setRoles]       = useState([]);

  const [modalOpen,    setModalOpen]    = useState(false);
  const [editTarget,   setEditTarget]   = useState(null);
  const [form,         setForm]         = useState(EMPTY_FORM);
  const [formErrors,   setFormErrors]   = useState({});
  const [saving,       setSaving]       = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting,     setDeleting]     = useState(false);

  // FIX: resetTarget kini punya state password sendiri
  const [resetTarget,   setResetTarget]   = useState(null);
  const [resetForm,     setResetForm]     = useState(EMPTY_RESET);
  const [resetErrors,   setResetErrors]   = useState({});
  const [showResetPw,   setShowResetPw]   = useState(false);
  const [showResetConf, setShowResetConf] = useState(false);
  const [resetting,     setResetting]     = useState(false);

  const debounceRef = useRef(null);

  useEffect(() => {
    api.get("/users/meta").then((res) => {
      if (res.data.success) {
        // Sembunyikan role superadmin dari pilihan — superadmin hanya boleh satu
        const filtered = res.data.data.roles.filter(
          (r) => r.name.toLowerCase() !== "superadmin"
        );
        setRoles(filtered);
      }
    }).catch(() => {});
  }, []);

  const fetchUsers = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const params = { page, per_page: 10 };
      if (search)     params.search  = search;
      // FIX: kirim role_id hanya jika ada nilai
      if (filterRole) params.role_id = filterRole;
      const res = await api.get("/users", { params });
      setUsers(res.data.data.data          ?? []);
      setTotalPages(res.data.data.last_page ?? 1);
    } catch {
      setError("Gagal memuat data user.");
    } finally {
      setLoading(false);
    }
  }, [page, search, filterRole]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);
  useEffect(() => { setPage(1); }, [search, filterRole]);

  const handleSearchInput = (e) => {
    const val = e.target.value;
    setSearchInput(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setSearch(val), 400);
  };

  const openCreate = () => {
    setEditTarget(null); setForm(EMPTY_FORM); setFormErrors({});
    setShowPassword(false); setModalOpen(true);
  };

  const openEdit = (user) => {
    setEditTarget(user);
    setForm({
      name:      user.name,
      email:     user.email,
      password:  "",
      // FIX: pastikan role_id bertipe string agar cocok dengan value MenuItem
      role_id:   user.role_id ? String(user.role_id) : "",
      is_active: user.is_active === undefined ? true : Boolean(user.is_active),
    });
    setFormErrors({}); setShowPassword(false); setModalOpen(true);
  };

  const closeModal = () => { setModalOpen(false); setEditTarget(null); };

  const handleSave = async () => {
    const errors = {};
    if (!form.name.trim())                    errors.name     = "Nama wajib diisi";
    if (!form.email.trim())                   errors.email    = "Email wajib diisi";
    if (!editTarget && !form.password)        errors.password = "Password wajib diisi untuk user baru";
    if (form.password && form.password.length < 8) errors.password = "Password minimal 8 karakter";
    if (!form.role_id)                        errors.role_id  = "Role wajib dipilih";
    if (Object.keys(errors).length) { setFormErrors(errors); return; }

    setSaving(true);
    try {
      const payload = { ...form };
      // Jangan kirim password kosong saat edit
      if (editTarget && !payload.password) delete payload.password;

      if (editTarget) {
        await api.put(`/users/${editTarget.id}`, payload);
        setSuccess("User berhasil diperbarui." + (form.password ? " User akan logout otomatis karena password diubah." : ""));
      } else {
        await api.post("/users", payload);
        setSuccess("User baru berhasil dibuat.");
      }
      closeModal(); fetchUsers();
    } catch (err) {
      const laravelErrors = err.response?.data?.errors || {};
      const mapped = {};
      Object.keys(laravelErrors).forEach((k) => { mapped[k] = laravelErrors[k][0]; });
      if (Object.keys(mapped).length) setFormErrors(mapped);
      else setError(err.response?.data?.message || "Gagal menyimpan user.");
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/users/${deleteTarget.id}`);
      setSuccess(`User "${deleteTarget.name}" telah dihapus.`);
      setDeleteTarget(null); fetchUsers();
    } catch (err) {
      setError(err.response?.data?.message || "Gagal menghapus user.");
    } finally { setDeleting(false); }
  };

  const openReset = (user) => {
    setResetTarget(user);
    setResetForm(EMPTY_RESET);
    setResetErrors({});
    setShowResetPw(false);
    setShowResetConf(false);
  };

  // FIX: Kirim password yang diisi admin ke backend
  const handleResetPassword = async () => {
    const errors = {};
    if (!resetForm.password)                         errors.password = "Password baru wajib diisi";
    else if (resetForm.password.length < 8)          errors.password = "Password minimal 8 karakter";
    if (resetForm.confirm !== resetForm.password)    errors.confirm  = "Konfirmasi password tidak cocok";
    if (Object.keys(errors).length) { setResetErrors(errors); return; }

    setResetting(true);
    try {
      await api.post(`/users/${resetTarget.id}/reset-password`, {
        password: resetForm.password,
      });
      setSuccess(`Password "${resetTarget.name}" berhasil direset. User telah dikeluarkan dari semua perangkat.`);
      setResetTarget(null);
    } catch (err) {
      setError(err.response?.data?.message || "Gagal mereset password.");
      setResetTarget(null);
    } finally { setResetting(false); }
  };

  const isSuperadmin = (user) => user.role?.name?.toLowerCase() === "superadmin";

  /* ── Render ── */
  return (
    <Box sx={{ p: 3, bgcolor: "#fff", minHeight: "100vh" }}>

      {/* Header */}
      <Box sx={{ mb: 4, display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
        <Box>
          <Typography sx={{ fontSize: "1.75rem", fontWeight: 800, color: C.text1, letterSpacing: "-0.04em", lineHeight: 1 }}>
            Manajemen Pengguna
          </Typography>
          <Typography sx={{ fontSize: "0.85rem", color: C.text3, mt: 0.8, fontWeight: 500 }}>
            Kelola hak akses dan akun administrator untuk seluruh modul sistem.
          </Typography>
        </Box>
        <Button
          variant="contained" startIcon={<AddIcon sx={{ fontSize: 18 }} />}
          onClick={openCreate}
          sx={{
            borderRadius: "10px", textTransform: "none", fontWeight: 700, fontSize: "0.8rem",
            bgcolor: C.primary, height: 40, px: 2, boxShadow: "none",
            "&:hover": { bgcolor: "#1f2937", boxShadow: "0 4px 12px rgba(0,0,0,0.15)" },
          }}
        >
          Tambah User
        </Button>
      </Box>

      {/* Alerts — auto-dismiss setelah 4 detik */}
      <AutoAlert severity="error"   message={error}   onClose={() => setError("")}   />
      <AutoAlert severity="success" message={success} onClose={() => setSuccess("")} />

      {/* Filter Bar */}
      <Box sx={{
        mb: 3, p: 1.5, bgcolor: "#f9fafb", borderRadius: "16px", border: "1px solid #f3f4f6",
        display: "flex", gap: 2, alignItems: "center",
      }}>
        <TextField
          placeholder="Cari nama atau email..."
          variant="outlined" size="small"
          value={searchInput} onChange={handleSearchInput}
          InputProps={{
            startAdornment: <SearchIcon sx={{ fontSize: 18, color: C.text3, mr: 1 }} />,
            sx: { borderRadius: "12px", bgcolor: "#fff" },
          }}
          sx={{ flex: 1, ...inputBase }}
        />
        <FormControl size="small" sx={{ minWidth: 160, ...inputBase }}>
          <Select
            value={filterRole} displayEmpty
            onChange={(e) => setFilterRole(e.target.value)}
            sx={{ borderRadius: "12px", bgcolor: "#fff" }}
          >
            <MenuItem value="">Semua Role</MenuItem>
            {/* FIX: value pakai String(r.id) agar tipe konsisten */}
            {roles.map((r) => <MenuItem key={r.id} value={String(r.id)}>{r.name}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #f3f4f6", borderRadius: "16px" }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: "#f9fafb" }}>
              {["User", "Email", "Status", "Role", "Aksi"].map((h) => (
                <TableCell key={h} sx={{ py: 1.5, fontWeight: 700, fontSize: "0.75rem", color: C.text3, textTransform: "uppercase" }}>
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} align="center" sx={{ py: 10 }}><CircularProgress size={24} /></TableCell></TableRow>
            ) : users.length === 0 ? (
              <TableRow><TableCell colSpan={5} align="center" sx={{ py: 10, color: C.text3 }}>Tidak ada user ditemukan.</TableCell></TableRow>
            ) : users.map((user) => (
              <TableRow key={user.id} sx={{ "&:hover": { bgcolor: "#fcfcfc" } }}>
                <TableCell sx={{ py: 1.5 }}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Avatar sx={{ width: 34, height: 34, fontSize: "0.8rem", fontWeight: 700, bgcolor: getAvatarColor(user.name) }}>
                      {getInitials(user.name)}
                    </Avatar>
                    <Typography sx={{ fontWeight: 600, fontSize: "0.85rem", color: C.text1 }}>{user.name}</Typography>
                  </Stack>
                </TableCell>
                <TableCell sx={{ fontSize: "0.85rem", color: C.text2 }}>{user.email}</TableCell>
                <TableCell>
                  <Chip
                    label={user.is_active ? "Aktif" : "Nonaktif"} size="small"
                    sx={{
                      fontWeight: 700, fontSize: "0.65rem",
                      bgcolor: user.is_active ? C.successBg : C.dangerBg,
                      color:   user.is_active ? C.success   : C.danger,
                    }}
                  />
                </TableCell>
                <TableCell><RoleChip roleName={user.role?.name} /></TableCell>
                <TableCell>
                  {isSuperadmin(user) ? (
                    <Tooltip title="Akun Utama (Proteksi Penuh)">
                      <ShieldOutlinedIcon sx={{ fontSize: 18, color: C.text3 }} />
                    </Tooltip>
                  ) : (
                    <Stack direction="row" spacing={1}>
                      <Tooltip title="Edit user">
                        <IconButton size="small" onClick={() => openEdit(user)} sx={{ color: C.text3, "&:hover": { color: C.accent } }}>
                          <EditIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Reset password">
                        <IconButton size="small" onClick={() => openReset(user)} sx={{ color: C.text3, "&:hover": { color: C.accent } }}>
                          <LockResetIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Hapus user">
                        <IconButton size="small" onClick={() => setDeleteTarget(user)} sx={{ color: C.text3, "&:hover": { color: C.danger } }}>
                          <DeleteIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {totalPages > 1 && (
          <Box p={2} sx={{ display: "flex", justifyContent: "center", borderTop: "1px solid #f3f4f6" }}>
            <Pagination count={totalPages} page={page} onChange={(_, v) => setPage(v)} size="small" />
          </Box>
        )}
      </TableContainer>

      {/* ── Dialog Create / Edit ── */}
      <Dialog open={modalOpen} onClose={closeModal} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "20px" } }}>
        <DialogTitle sx={{ fontWeight: 800, fontSize: "1.1rem" }}>
          {editTarget ? "Perbarui User" : "Daftarkan User Baru"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <TextField
              label="Nama Lengkap" fullWidth
              value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              error={!!formErrors.name} helperText={formErrors.name}
              sx={inputBase}
            />
            <TextField
              label="Email" fullWidth
              value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              error={!!formErrors.email} helperText={formErrors.email}
              sx={inputBase}
            />

            <Box>
              <TextField
                label={editTarget ? "Ganti Password (opsional)" : "Password"}
                type={showPassword ? "text" : "password"} fullWidth
                value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                error={!!formErrors.password}
                helperText={formErrors.password || (editTarget ? "Kosongkan jika tidak ingin mengubah password." : "")}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowPassword(!showPassword)}>
                        {showPassword
                          ? <VisibilityOffOutlinedIcon sx={{ fontSize: 16 }} />
                          : <VisibilityOutlinedIcon   sx={{ fontSize: 16 }} />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={inputBase}
              />
            </Box>

            <FormControl fullWidth error={!!formErrors.role_id} sx={inputBase}>
              <Select
                value={form.role_id} displayEmpty
                onChange={(e) => setForm({ ...form, role_id: e.target.value })}
                sx={{ borderRadius: "12px" }}
              >
                <MenuItem value="" disabled><em style={{ color: C.text3 }}>Pilih Role</em></MenuItem>
                {roles.map((r) => <MenuItem key={r.id} value={String(r.id)}>{r.name}</MenuItem>)}
              </Select>
              {formErrors.role_id && (
                <Typography sx={{ fontSize: "0.75rem", color: C.danger, mt: 0.5, ml: 1.5 }}>{formErrors.role_id}</Typography>
              )}
            </FormControl>

            <Box sx={{ p: 2, bgcolor: "#f9fafb", borderRadius: "12px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Box>
                <Typography sx={{ fontSize: "0.85rem", fontWeight: 700 }}>Akses Login</Typography>
                <Typography sx={{ fontSize: "0.75rem", color: C.text3 }}>Status aktifitas akun saat ini.</Typography>
              </Box>
              <Switch checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={closeModal} sx={{ textTransform: "none", fontWeight: 700, color: C.text3 }}>Batal</Button>
          <Button
            variant="contained" onClick={handleSave} disabled={saving}
            sx={{ bgcolor: C.primary, textTransform: "none", fontWeight: 700, borderRadius: "10px", px: 3 }}
          >
            {saving ? "Menyimpan..." : "Simpan Data"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog Reset Password ── */}
      <Dialog
        open={!!resetTarget} onClose={() => !resetting && setResetTarget(null)}
        PaperProps={{ sx: { borderRadius: "20px", maxWidth: 420 } }}
        fullWidth
      >
        <Box sx={{ p: 4 }}>
          {/* Icon header */}
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <Box sx={{
              width: 64, height: 64, bgcolor: C.accentBg, borderRadius: "50%",
              display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 2,
            }}>
              <LockResetIcon sx={{ fontSize: 32, color: C.accent }} />
            </Box>
            <Typography sx={{ fontWeight: 800, fontSize: "1.2rem", mb: 0.5 }}>Reset Password</Typography>
            <Typography sx={{ fontSize: "0.85rem", color: C.text2 }}>
              Tentukan password baru untuk <b>{resetTarget?.name}</b>
            </Typography>
          </Box>

          <Stack spacing={2}>
            {/* Password baru */}
            <Box>
              <TextField
                label="Password Baru" fullWidth size="small"
                type={showResetPw ? "text" : "password"}
                value={resetForm.password}
                onChange={(e) => setResetForm({ ...resetForm, password: e.target.value })}
                error={!!resetErrors.password} helperText={resetErrors.password}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowResetPw(!showResetPw)}>
                        {showResetPw
                          ? <VisibilityOffOutlinedIcon sx={{ fontSize: 16 }} />
                          : <VisibilityOutlinedIcon   sx={{ fontSize: 16 }} />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={inputBase}
              />
            </Box>

            {/* Konfirmasi password */}
            <TextField
              label="Konfirmasi Password" fullWidth size="small"
              type={showResetConf ? "text" : "password"}
              value={resetForm.confirm}
              onChange={(e) => setResetForm({ ...resetForm, confirm: e.target.value })}
              error={!!resetErrors.confirm} helperText={resetErrors.confirm}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowResetConf(!showResetConf)}>
                      {showResetConf
                        ? <VisibilityOffOutlinedIcon sx={{ fontSize: 16 }} />
                        : <VisibilityOutlinedIcon   sx={{ fontSize: 16 }} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={inputBase}
            />

            {/* Info force logout */}
            <Box sx={{
              p: 1.5, bgcolor: "#FEFCE8", border: "1px solid #FEF08A",
              borderRadius: "12px", display: "flex", gap: 1.5,
            }}>
              <InfoOutlinedIcon sx={{ fontSize: 18, color: "#A16207", mt: 0.2, flexShrink: 0 }} />
              <Typography sx={{ fontSize: "0.75rem", color: "#854D0E", fontWeight: 600 }}>
                User akan otomatis dikeluarkan (Force Logout) dari semua sesi aktif setelah reset.
              </Typography>
            </Box>

            {/* Tombol aksi */}
            <Stack direction="row" spacing={2} sx={{ pt: 1 }}>
              <Button
                fullWidth variant="outlined"
                onClick={() => setResetTarget(null)} disabled={resetting}
                sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700, borderColor: C.border, color: C.text2 }}
              >
                Batal
              </Button>
              <Button
                fullWidth variant="contained"
                onClick={handleResetPassword} disabled={resetting}
                sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700, bgcolor: C.accent, boxShadow: "none" }}
              >
                {resetting ? "Memproses..." : "Reset Password"}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Dialog>

      {/* ── Dialog Delete ── */}
      <Dialog
        open={!!deleteTarget} onClose={() => !deleting && setDeleteTarget(null)}
        PaperProps={{ sx: { borderRadius: "20px", maxWidth: 400 } }}
      >
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Box sx={{
            width: 64, height: 64, bgcolor: C.dangerBg, borderRadius: "50%",
            display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 2,
          }}>
            <DeleteIcon sx={{ fontSize: 30, color: C.danger }} />
          </Box>
          <Typography sx={{ fontWeight: 800, fontSize: "1.2rem", mb: 1 }}>Hapus Pengguna?</Typography>
          <Typography sx={{ fontSize: "0.85rem", color: C.text2, mb: 4 }}>
            Tindakan ini tidak bisa dibatalkan. Akun <b>{deleteTarget?.name}</b> akan dihapus permanen.
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button
              fullWidth variant="outlined"
              onClick={() => setDeleteTarget(null)} disabled={deleting}
              sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700, borderColor: C.border, color: C.text2 }}
            >
              Batal
            </Button>
            <Button
              fullWidth variant="contained"
              onClick={handleDelete} disabled={deleting}
              sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700, bgcolor: C.danger, boxShadow: "none" }}
            >
              {deleting ? "Menghapus..." : "Hapus Akun"}
            </Button>
          </Stack>
        </Box>
      </Dialog>

    </Box>
  );
}